import React, { useCallback, useMemo, useRef } from "react";
import { CellContext } from "@tanstack/react-table";
import type { ViewTableFeatures } from "@design-system/ViewTable/core/table";
import classNames from "classnames";
import { z } from "zod";
import { Counterparty } from "@common/types/counterparty";
import { CounterpartyFieldReviewDto } from "@common/schemas/counterpartyFieldReview.schema";
import { CounterpartyReviewField } from "@common/types/counterpartyFieldReview";
import {
    addCounterpartyFieldReviews,
    updateCounterpartyFieldReviewsInCache,
} from "@api/counterparty.api";
import { ViewTableCell } from "@design-system/ViewTable/ViewTableCell";
import { HStack } from "@design-system/HStack/HStack";
import { ToggleGroup } from "@design-system/ToggleGroup/ToggleGroup";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import { useUser } from "@hooks/useUser";
import { useScope } from "@state/scopeContext.lib";
import { resolveColumnVariant } from "@design-system/ViewTable/core/resolveColumnVariant";
import { CounterpartyDirectoryViewRow } from "../counterpartyDirectoryViewTypes";
import { useUpdateCounterpartyFieldMutation } from "../useUpdateCounterpartyFieldMutation";
import { CounterpartyFieldReviewButton } from "./CounterpartyFieldReviewButton";
import {
    isCounterpartyFieldReviewedByUser,
    mergeCounterpartyFieldReviews,
} from "./counterpartyFieldReviewUtils";
import { useIsTableReadonly } from "@hooks/useIsReadonlyOrLocked";
import styles from "./CounterpartyEligible1099Cell.module.scss";

const Eligible1099CellVariantSchema = z.enum(["default", "review"]);
type Eligible1099CellVariant = z.infer<typeof Eligible1099CellVariantSchema>;
type Eligible1099ToggleValue = "yes" | "no";

interface CounterpartyEligible1099CellInnerProps {
    counterparty: Counterparty;
    fieldReviews: CounterpartyFieldReviewDto[] | undefined;
    isReviewVariant: boolean;
}

const CounterpartyEligible1099CellInner: React.FC<CounterpartyEligible1099CellInnerProps> =
    React.memo(({ counterparty, fieldReviews, isReviewVariant }) => {
        const user = useUser();
        const { workspaceId } = useScope();
        const { error: errorToast } = useToaster();
        const { updateField, isLoading } = useUpdateCounterpartyFieldMutation({
            counterparty,
            errorMessage: "Failed to update 1099 eligibility",
        });

        const isReviewed = useMemo(
            () =>
                isCounterpartyFieldReviewedByUser(
                    fieldReviews,
                    CounterpartyReviewField.ELIGIBLE_1099,
                    user?.id,
                ),
            [fieldReviews, user?.id],
        );

        const toggleValue: Eligible1099ToggleValue =
            counterparty.isEligible1099 === true ? "yes" : "no";
        const isMarkingReviewedRef = useRef(false);

        const markEligible1099Reviewed = useCallback(async () => {
            if (
                !isReviewVariant ||
                !workspaceId ||
                isReviewed ||
                isMarkingReviewedRef.current
            ) {
                return;
            }

            isMarkingReviewedRef.current = true;
            try {
                const reviews = await addCounterpartyFieldReviews(
                    workspaceId,
                    counterparty.id,
                    [CounterpartyReviewField.ELIGIBLE_1099],
                );
                updateCounterpartyFieldReviewsInCache(
                    workspaceId,
                    counterparty.id,
                    (existing) =>
                        mergeCounterpartyFieldReviews(existing, reviews),
                );
            } catch {
                errorToast(
                    "Failed to mark 1099 eligibility as reviewed. Please try again.",
                );
            } finally {
                isMarkingReviewedRef.current = false;
            }
        }, [
            isReviewVariant,
            workspaceId,
            isReviewed,
            counterparty.id,
            errorToast,
        ]);

        const handleEligibilityChange = useCallback(
            async (next: Eligible1099ToggleValue | null) => {
                if (next === toggleValue || next === null) {
                    await markEligible1099Reviewed();
                    return;
                }

                let didUpdate = false;
                if (next === "yes") {
                    didUpdate = await updateField({ isEligible1099: true });
                } else if (next === "no") {
                    didUpdate = await updateField({ isEligible1099: null });
                }

                if (!didUpdate) {
                    return;
                }

                await markEligible1099Reviewed();
            },
            [updateField, toggleValue, markEligible1099Reviewed],
        );

        const handleToggleItemClick = useCallback(
            (clicked: Eligible1099ToggleValue) => {
                if (clicked !== toggleValue) {
                    return;
                }

                void markEligible1099Reviewed();
            },
            [toggleValue, markEligible1099Reviewed],
        );

        const eligibilityControl = (
            <ToggleGroup.Root<Eligible1099ToggleValue>
                value={toggleValue}
                onValueChange={handleEligibilityChange}
                size="lg"
                disabled={isLoading}
                className={classNames({
                    [styles.reviewVariant]: isReviewVariant,
                    [styles.isReviewed]: isReviewVariant && isReviewed,
                })}
            >
                <ToggleGroup.Item
                    value="yes"
                    onClick={() => handleToggleItemClick("yes")}
                >
                    Yes
                </ToggleGroup.Item>
                <ToggleGroup.Item
                    value="no"
                    onClick={() => handleToggleItemClick("no")}
                >
                    No
                </ToggleGroup.Item>
            </ToggleGroup.Root>
        );

        if (!isReviewVariant) {
            return (
                <ViewTableCell captureClicks>
                    {eligibilityControl}
                </ViewTableCell>
            );
        }

        return (
            <ViewTableCell captureClicks className={styles.reviewCell}>
                <HStack gap={8} align="center">
                    {eligibilityControl}
                    <CounterpartyFieldReviewButton
                        counterpartyId={counterparty.id}
                        fieldReviews={fieldReviews}
                        field={CounterpartyReviewField.ELIGIBLE_1099}
                        reviewTooltip="Review 1099 eligibility"
                    />
                </HStack>
            </ViewTableCell>
        );
    });

CounterpartyEligible1099CellInner.displayName =
    "CounterpartyEligible1099CellInner";

export const CounterpartyEligible1099Cell: React.FC<
    CellContext<ViewTableFeatures, CounterpartyDirectoryViewRow, unknown>
> = React.memo(({ row, column, table }) => {
    const isReadonly = useIsTableReadonly(table);
    const { counterparty, isAccountRow, isNoCounterparty, fieldReviews } =
        row.original;

    const variant: Eligible1099CellVariant = resolveColumnVariant({
        meta: column.columnDef.meta,
        schema: Eligible1099CellVariantSchema,
        fallback: "default",
    });
    const isReviewVariant = variant === "review";

    if (isAccountRow || isNoCounterparty || !counterparty) {
        return <ViewTableCell />;
    }

    if (isReadonly) {
        return (
            <ViewTableCell>
                {counterparty.isEligible1099 === true ? "Yes" : "No"}
            </ViewTableCell>
        );
    }

    return (
        <CounterpartyEligible1099CellInner
            counterparty={counterparty}
            fieldReviews={fieldReviews}
            isReviewVariant={isReviewVariant}
        />
    );
});

CounterpartyEligible1099Cell.displayName = "CounterpartyEligible1099Cell";
